import { AddCircleOutline, Delete, Email, Phone, Star, StarBorder } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography, useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState, useCallback, useRef } from "react";
import { colors, alphaVar } from "../../../../ui/colors";
import { SASInputStyles } from "../../../../ui/styles/inputStyles";
import { InputMultipleContacts, PhoneContactItem } from "./InputMultipleContacts";

export interface EmailContactItem {
  id: string | number;
  value: string;
  tag?: string;
  isDefault?: boolean;
}

export const REGEX_EMAIL_ONLY = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SectionTitle = ({ children, icon = true }: { children: React.ReactNode; icon?: boolean }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {icon && (
        <Box
          sx={{
            width: 4,
            height: 24,
            bgcolor: colors.orange.base,
            borderRadius: 2,
            mr: 2,
          }}
        />
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: colors.gray[800],
          letterSpacing: 0.5,
        }}
      >
        {children}
      </Typography>
    </Box>
    <Divider sx={{ borderColor: colors.gray[200], opacity: 0.8 }} />
  </Box>
);

let nextIdCounter = 1;
const generateUniqueId = (prefix: string) => `${prefix}-${Date.now()}-${nextIdCounter++}`;

const parseInitialEmails = (emails: any[]): EmailContactItem[] => {
  if (Array.isArray(emails) && emails.length > 0) {
    return emails.map((e, idx) => ({
      id: generateUniqueId("email"),
      value: typeof e === "string" ? e : e?.value || e?.email || "",
      tag: (typeof e === "object" && e?.tag) || "other",
      isDefault: typeof e === "object" && e?.isDefault !== undefined ? !!e.isDefault : idx === 0,
    }));
  }
  return [{ id: generateUniqueId("email"), value: "", tag: "other", isDefault: true }];
};

const parseInitialPhones = (phones: any[]): PhoneContactItem[] => {
  if (Array.isArray(phones) && phones.length > 0) {
    return phones.map((p, idx) => ({
      id: generateUniqueId("phone"),
      countryCode: p?.countryCode || "US",
      dialCode: p?.dialCode || "+1",
      phone: (p?.phone || "").replace(/\D/g, ""),
      tag: p?.tag || "other",
      isDefault: p?.isDefault !== undefined ? !!p.isDefault : idx === 0,
    }));
  }
  return [{ id: generateUniqueId("phone"), countryCode: "US", dialCode: "+1", phone: "", tag: "other", isDefault: true }];
};

interface ContactInformationFieldsProps {
  initialEmails?: any[];
  initialPhones?: any[];
  onChange?: (state: { emails: any[]; phones: any[]; phoneNumbers: any[]; isValid: boolean }) => void;
  disabled?: boolean;
  requireEither?: boolean;
  showErrors?: boolean;
}

export const ContactInformationFields = ({
  initialEmails = [],
  initialPhones = [],
  onChange,
  disabled = false,
  requireEither = true,
  showErrors = false,
}: ContactInformationFieldsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const isXs = useMediaQuery("(max-width:600px)");

  const [emails, setEmails] = useState<EmailContactItem[]>(() => parseInitialEmails(initialEmails));
  const [phones, setPhones] = useState<PhoneContactItem[]>(() => parseInitialPhones(initialPhones));
  const [touchedEmailIds, setTouchedEmailIds] = useState<Set<string | number>>(new Set());

  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (Array.isArray(initialEmails) && initialEmails.length > 0) {
      setEmails(parseInitialEmails(initialEmails));
    }
    if (Array.isArray(initialPhones) && initialPhones.length > 0) {
      setPhones(parseInitialPhones(initialPhones));
    }
  }, [initialEmails, initialPhones]);

  // Validation function
  const validateFields = useCallback(
    (emailList: EmailContactItem[], phoneList: PhoneContactItem[]) => {
      const emailErrors: Record<string | number, string> = {};
      const phoneErrors: Record<string | number, string> = {};

      const hasValidEmail = emailList.some((e) => e.value && REGEX_EMAIL_ONLY.test(e.value.trim()));
      const hasValidPhone = phoneList.some((p) => {
        const clean = (p.phone || "").replace(/\D/g, "");
        return clean.length >= 10 && clean.length <= 14;
      });

      // Validate Emails
      emailList.forEach((e) => {
        const val = (e.value || "").trim();
        if (!val) {
          if (!requireEither) {
            emailErrors[e.id] = "Email is required";
          }
        } else if (!REGEX_EMAIL_ONLY.test(val)) {
          emailErrors[e.id] = "Invalid email address";
        }
      });

      // Validate Phones
      phoneList.forEach((p) => {
        const clean = (p.phone || "").replace(/\D/g, "");
        if (!clean) {
          if (!requireEither) {
            phoneErrors[p.id] = "Phone number is required";
          }
        } else if (clean.length < 10) {
          phoneErrors[p.id] = "Phone number must be at least 10 digits";
        } else if (clean.length > 14) {
          phoneErrors[p.id] = "Invalid phone number";
        }
      });

      const hasErrors = Object.keys(emailErrors).length > 0 || Object.keys(phoneErrors).length > 0;
      const satisfiesRequirement = !requireEither || hasValidEmail || hasValidPhone;
      const isValid = !hasErrors && satisfiesRequirement;

      return { emailErrors, phoneErrors, isValid };
    },
    [requireEither]
  );

  // Notify parent component on state change
  const notifyParent = useCallback(
    (currentEmails: EmailContactItem[], currentPhones: PhoneContactItem[]) => {
      const { isValid } = validateFields(currentEmails, currentPhones);

      const formattedEmails = currentEmails.map((e, idx) => ({
        tag: e.tag || "other",
        email: e.value ? e.value.trim() : "",
        value: e.value ? e.value.trim() : "",
        isDefault: e.isDefault !== undefined ? !!e.isDefault : idx === 0,
      }));

      const formattedPhones = currentPhones.map((p, idx) => {
        const cleanPhone = (p.phone || "").replace(/\D/g, "");
        const dialCode = p.dialCode || "+1";
        return {
          phone: cleanPhone,
          dialCode: dialCode,
          isDefault: p.isDefault !== undefined ? !!p.isDefault : idx === 0,
          countryCode: p.countryCode || "US",
          phoneNumber: `${dialCode}${cleanPhone}`,
          tag: p.tag || "other",
        };
      });

      onChange?.({
        emails: formattedEmails,
        phones: formattedPhones,
        phoneNumbers: formattedPhones,
        isValid,
      });
    },
    [validateFields, onChange]
  );

  useEffect(() => {
    notifyParent(emails, phones);
  }, [emails, phones, notifyParent]);

  const { emailErrors, phoneErrors } = validateFields(emails, phones);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAddContact = (type: "email" | "phone") => {
    if (type === "email") {
      const newEmail: EmailContactItem = {
        id: generateUniqueId("email"),
        value: "",
        tag: "other",
        isDefault: emails.length === 0,
      };
      setEmails((prev) => [...prev, newEmail]);
    } else if (type === "phone") {
      const newPhone: PhoneContactItem = {
        id: generateUniqueId("phone"),
        countryCode: "US",
        dialCode: "+1",
        phone: "",
        tag: "other",
        isDefault: phones.length === 0,
      };
      setPhones((prev) => [...prev, newPhone]);
    }
    handleCloseMenu();
  };

  const handleRemoveEmail = (id: string | number) => {
    setEmails((prev) => {
      const updated = prev.filter((item) => item.id !== id);
      if (updated.length > 0 && !updated.some((e) => e.isDefault)) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  const handleRemovePhone = (id: string | number) => {
    setPhones((prev) => {
      const updated = prev.filter((item) => item.id !== id);
      if (updated.length > 0 && !updated.some((p) => p.isDefault)) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  const handleSetDefaultEmail = (id: string | number) => {
    setEmails((prev) =>
      prev.map((item) => ({
        ...item,
        isDefault: item.id === id,
      }))
    );
  };

  const handleSetDefaultPhone = (id: string | number) => {
    setPhones((prev) =>
      prev.map((item) => ({
        ...item,
        isDefault: item.id === id,
      }))
    );
  };

  const handleEmailChange = (id: string | number, value: string) => {
    setTouchedEmailIds((prev) => new Set(prev).add(id));
    setEmails((prev) => prev.map((item) => (item.id === id ? { ...item, value } : item)));
  };

  const handlePhoneChange = (updatedItem: PhoneContactItem) => {
    setPhones((prev) => prev.map((item) => (item.id === updatedItem.id ? updatedItem : item)));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <SectionTitle icon={false}>Contact Information</SectionTitle>
        </Box>
        {!disabled && (
          <Button
            onClick={handleOpenMenu}
            startIcon={<AddCircleOutline />}
            sx={{
              color: colors.orange.base,
              fontWeight: 600,
              textTransform: "none",
              border: `2px solid ${colors.orange.base}`,
              borderRadius: 2,
              px: 3,
              mt: isXs ? -2 : 2,
              ml: isXs ? 2 : 2,
              lineHeight: 1,
              "&:hover": {
                bgcolor: colors.orange[50],
                borderColor: colors.orange[600],
              },
            }}
          >
            Add Contact
          </Button>
        )}

        {/* Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                zIndex: 999999999,
                minWidth: "160px",
                position: "absolute",
              },
            },
          }}
          sx={{
            zIndex: 999999999,
            "& .MuiBackdrop-root": {
              zIndex: 999999998,
            },
          }}
        >
          <MenuItem
            onClick={() => handleAddContact("email")}
            sx={{
              py: 1.5,
              "&:hover": {
                bgcolor: colors.orange[50],
              },
            }}
          >
            <ListItemIcon>
              <Email sx={{ color: colors.orange.base }} />
            </ListItemIcon>
            <ListItemText
              primary="Email"
              primaryTypographyProps={{
                fontWeight: 500,
              }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => handleAddContact("phone")}
            sx={{
              py: 1.5,
              "&:hover": {
                bgcolor: colors.orange[50],
              },
            }}
          >
            <ListItemIcon>
              <Phone sx={{ color: colors.orange.base }} />
            </ListItemIcon>
            <ListItemText
              primary="Phone"
              primaryTypographyProps={{
                fontWeight: 500,
              }}
            />
          </MenuItem>
        </Menu>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          width: "100%",
        }}
      >
        {/* Emails Section */}
        <Box sx={{ flex: "1 1 340px", minWidth: "280px" }}>
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1px solid rgba(12,12,12,0.08)`,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              boxShadow: "0 2px 8px rgba(12,12,12,0.02)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: alphaVar(colors.orange.base, 0.1),
                  color: colors.orange.base,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Email sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={600} color={colors.gray[800]}>
                Emails
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {emails.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    border: `2px dashed ${colors.gray[300]}`,
                    borderRadius: 2,
                    bgcolor: colors.gray[50],
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No emails added yet
                  </Typography>
                </Box>
              ) : (
                <AnimatePresence>
                  {emails.map((field, index) => {
                    const err = emailErrors[field.id] || "";
                    const isTouched = touchedEmailIds.has(field.id);
                    const hasError = !!(showErrors || isTouched) && !!err;

                    return (
                      <motion.div
                        key={field.id}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            alignItems: "flex-start",
                            width: "100%",
                            mb: 2,
                          }}
                        >
                          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
                            <TextField
                              fullWidth
                              placeholder="email@example.com"
                              disabled={disabled}
                              value={field.value}
                              onChange={(e) => handleEmailChange(field.id, e.target.value)}
                              onBlur={() => setTouchedEmailIds((prev) => new Set(prev).add(field.id))}
                              error={hasError}
                              helperText={hasError ? err : ""}
                              sx={{
                                ...SASInputStyles,
                                "& .MuiOutlinedInput-root": {
                                  ...SASInputStyles["& .MuiOutlinedInput-root"],
                                  height: "48px",
                                },
                              }}
                            />
                          </Box>

                          {/* Actions Container */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: "0 0 auto",
                              ml: "auto",
                              height: "48px",
                            }}
                          >
                            {/* Default Email Indicator */}
                            <IconButton
                              onClick={() => !disabled && handleSetDefaultEmail(field.id)}
                              disabled={disabled}
                              sx={{
                                color: field.isDefault ? colors.orange.base : colors.gray[300],
                                "&:hover": {
                                  color: colors.orange.base,
                                },
                              }}
                              title="Mark as default email"
                            >
                              {field.isDefault ? <Star sx={{ fontSize: 20 }} /> : <StarBorder sx={{ fontSize: 20 }} />}
                            </IconButton>

                            {!disabled && (
                              <IconButton
                                onClick={() => handleRemoveEmail(field.id)}
                                size="small"
                                sx={{
                                  p: 0.5,
                                  color: colors.danger[600],
                                  transition: "all 0.2s ease",
                                  "&:hover": { bgcolor: colors.orange[50] },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </Box>
          </Box>
        </Box>

        {/* Phone Numbers Section */}
        <Box sx={{ flex: "1 1 340px", minWidth: "280px" }}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: "12px",
              border: `1px solid rgba(12,12,12,0.08)`,
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              boxShadow: "0 2px 8px rgba(12,12,12,0.02)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: alphaVar(colors.orange.base, 0.1),
                  color: colors.orange.base,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Phone sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="subtitle1" fontWeight={600} color={colors.gray[800]}>
                Phone Numbers
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              {phones.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    border: `2px dashed ${colors.gray[300]}`,
                    borderRadius: 2,
                    bgcolor: colors.gray[50],
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No phone numbers added yet
                  </Typography>
                </Box>
              ) : (
                <AnimatePresence>
                  {phones.map((field, index) => (
                    <motion.div
                      key={field.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Box>
                        <InputMultipleContacts
                          item={field}
                          index={index}
                          disabled={disabled}
                          error={phoneErrors[field.id] || ""}
                          showErrors={showErrors}
                          onChange={handlePhoneChange}
                          onRemove={() => handleRemovePhone(field.id)}
                          onSetDefault={() => handleSetDefaultPhone(field.id)}
                        />
                      </Box>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
